import React, { ChangeEvent, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import useFileUploadRef, { HiddenFileInputModel } from '../../hooks/useFileUploadRef/useFileUploadRef';
import { RenderViewChildren } from '../../types/RenderViewChildren';
import { FileToken } from '../../types/FileToken';
import { UIProps } from '../../types/StyledProps';
import { DropEventProps } from '../../types/DropEventProps';
import { ValidationResults } from '../../../lower-fns/validate';
import useNotifications from '../../../../hooks/useNotifications/useNotifications';
import { useDragNDropContext } from '../../../../hooks/useDragNDropContext/useDragNDropContext';
import { NotificationType, UserNotification } from '../../../../hooks/useNotifications/UserNotification';
import FileUploadView from './file-upload-view/file-upload-view';
import useFileUploadModel from './file-upload-model';

export type FileUploadProviderProps = React.PropsWithoutRef<{
  render?: (props: FileUploadViewProps) => JSX.Element;
  renderChildren?: RenderViewChildren<FileUploadViewModel>;
} & UIProps>;

export type FileUploadViewModel = {
  isDragging: boolean;
  tokens: FileToken[];
  handleUploadBtn: (event: ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
} & HiddenFileInputModel;

export type FileUploadViewProps = {
  viewModel: FileUploadViewModel;
  renderChildren?: RenderViewChildren<FileUploadViewModel>;
  dropEvents: DropEventProps;
} & UIProps;

function FileUploadProvider({
  render: ViewComponent = FileUploadView,
  renderChildren,
  ...props
}: FileUploadProviderProps) {
  const { uploadRef, showUploadDialog } = useFileUploadRef();

  const { setIsDragging: setContextIsDragging, setIsDropping } = useDragNDropContext();

  const [isDragging, setIsDragging] = React.useState<boolean>(false);

  const [isUploading, setIsUploading] = React.useState(false);

  const { notify, clearNotifications } = useNotifications();

  const handleErrors = (item: FileToken, results: ValidationResults) => {
    clearNotifications();
    const { errors } = results;

    const keys = Object.keys(errors);

    for (let fileIndex = 0; fileIndex < keys.length; fileIndex++) {
      const fileErrorMessages = errors[keys[fileIndex]];

      for (let errorIndex = 0; errorIndex < fileErrorMessages.length; errorIndex++) {
        const errorMessage: UserNotification = {
          message: fileErrorMessages[errorIndex],
          notificationType: NotificationType.Error,
          title: 'The file upload failed!',
        };

        notify(errorMessage);
      }
    }
  };

  const store = useFileUploadModel(handleErrors);

  const handleUploadBtn = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    e.preventDefault();
    setIsUploading(true);
    const { files } = e.target;

    // eslint-disable-next-line no-void
    void store.enqueue(files).then(() => {
      setIsUploading(false);
    });
  };

  const viewModel: FileUploadViewModel = {
    handleUploadBtn,
    isDragging,
    isUploading,
    showUploadDialog,
    tokens: store.tokens,
    uploadRef,
  };

  const dropEvents: DropEventProps = {
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDragOver: (event) => {
      event.preventDefault();
      setIsDragging(true);
    },
    onDrop: (event) => {
      event.preventDefault();
      setIsUploading(true);
      store.enqueue(event.dataTransfer.files).then(() => {
        setIsUploading(false);
      }).catch(() => {
        setIsUploading(false);
      });
      setIsDragging(false);
      setIsDropping(true);
    },
  };

  useEffect(() => {
    setContextIsDragging(isDragging);

    return () => {
      // do nothing
    };
  }, [isDragging, setContextIsDragging]);

  return (
    <ViewComponent
      viewModel={viewModel}
      renderChildren={renderChildren}
      dropEvents={dropEvents}
      {...props}
    />
  );
}

export default observer(FileUploadProvider);
